<!-- prettier-ignore -->
<template>
  <aw-drag
    tag="tr"
    :enable="edit && parent.data.sort === 'rank' && !element.file?.deleted"
    :drag-item="element"
    #default="{ dragHandle }"
    class="DocumentElementFilesFile"
    :class="{
      DocumentPlaceholderTableRow: index === -1,
      'TableRow--TableTagsShow': tableTagsShow,
      DocumentHasPlaceholder: element.placeholder,
      DocumentHighlightSearch: $route.hash === '#el' + element.id,
    }"
    :id="'el' + element.id"
  >
    <tippy 
      v-if="file?.deleted"
      :content="$t('File was deleted')" 
      to="parent"
    />
    <tippy 
      v-else-if="file_upload?.id"
      :content="$t('Waiting for download file from external storage')" 
      to="parent"
    />
    <tippy 
      v-else-if="antivirusState === 'hasVirus'"
      :content="$t('File has virus')" 
      theme='warning'
      to="parent"
    />
    <!-- Duplikace oznacovani kvuli mobilu, zmena vyjizdeni https://aw.andweb.cz/#/task/4275 -->
    <td class="py-2 px-0 whitespace-nowrap FileFirstCell relative">
      <span class="FileCircleWrapper absolute left-0 transform translate-x-0.5">
        <span
          class="DocumentCircle DocumentCircle--SM mr-2.5"
          :class="{
            'DocumentCircle--active': bcSelectedStore.selected.has(element.id)
          }"
          @click.stop="bcSelectedStore.select(element)"
        ></span>
      </span>
    </td>

    <td
      class="min-w-50 w-14 p-0 relative HasTableTags"
      @mouseover="tableTagsShow = true"
      @mouseout="tableTagsShow = false"
    >
      <div 
        class="z-1 text-center"
        :class="{'bg-checkered-md': blackBackground && !file?.deleted && antivirusState !== 'hasVirus'}"
      >
        <aw-icon 
          v-if="antivirusState === 'hasVirus'"
          icon="bug-report" 
          class="w-10 h-10 text-red-600"
        />
        <aw-icon 
          v-else-if="file?.deleted"
          icon="trash-slim" 
          class="w-10 h-10 text-gray-300"
        />
        <div 
          v-else-if="antivirusState === 'waiting' || !hasPreview"
          class="flex-row items-center justify-center text-center"
        >
          <file-icon :ext="fileExt" size="small" />
        </div>

        <img 
          v-else-if="hasPreview"
          v-show="imagePreviewLoaded"
          :src="imagePreviewLoaded ? element.file?.previews['500x500'] : undefined" 
          alt="" 
          class="cursor-pointer"
          ref="imagePreview"
          @click="openImage()"
        />
        <div
          v-else-if="!imagePreviewLoaded"
          class="flex items-center justify-center absolute inset-0"
        >
          <aw-icon icon="loader" class="h-8 w-8 text-bc-blue " />
        </div>
      </div>

      <div v-if="bcDocumentStore.edit && !isFileRequest" class="TableTags">
        <div class="flex space-x-4" v-if="tableTagsShow">
          <b>
            {{ $t('Tags') }}:
          </b>
          <bc-select-table-tag multiple v-model="tagModel" />
        </div>
      </div>

      <div
        v-if="!bcDocumentStore.edit && element?.file?.tags.length"
        class="TableTags z-2"
      >
        <div class="flex space-x-4">
          <b>
            {{ $t('Tags') }}:
          </b>
          <div class="BcSelectMultiTags">
            <span
              v-for="tag in element?.file?.tags"
              :key="tag"
              class="TableTags__Tag"
            >
              {{ tag }}
            </span>
          </div>
        </div>
      </div>
    </td>
    <td
      class="py-2 px-0 md:px-3 relative Table__Cell-Name"
      style="width: 80%;"
      @mouseover="tableTagsShow = true"
      @mouseout="tableTagsShow = false"
    >
        <contenteditable
          v-if="edit && !file?.deleted"
          tag="span"
          v-model="fileName"
          :contenteditable="edit"
          :noNL="true"
          :noHTML="true"
          :alwaysSelectAll="element.file?.name"
          class="pl-2"
          @updated="updated = 'name', blurNameInput()"
          @blur="blurNameInput()"
          @focused="focusNameInput"
        />

        <a
          v-else-if="hasPreview && antivirusState !== 'hasVirus' && !file?.deleted"
          class="truncate pl-2"
          href="#"
          @click.stop.prevent="openImage()"
        >
          {{ fileName }}
        </a>

        <a
          v-else-if="data.can_download && isDownloadable"
          class="truncate pl-2"
          :href="file?.download_url.original"
          @click.stop.prevent="downloadFile"
        >
          {{ fileName }}
        </a>

        <span v-else
          class="truncate pl-2"
          :class="antivirusState === 'hasVirus' ? 'text-red-600' : file?.deleted || file_upload?.id ? 'text-gray-300' : 'text-gray-800'"
        >{{ fileName }}</span>
    </td>
    <td
      v-if="parent.type === 'preview' && parent.children.length > 1"
      class="py-2 px-3 text-center whitespace-nowrap hidden sm:table-cell relative"
    >
      <span
        class="px-2 text-gray-400 hover:text-bc-blue cursor-pointer"
        :class="{ 
          'text-bc-blue': eyeSelected,
          '!text-gray-300': (file?.deleted || file_upload?.id) && !eyeSelected
        }"
        @click="setPreviewImageId(element.file?.id)"
      >
        <aw-icon
          icon="eye-opened"
          class="w-4 h-4 current-fill"
        />
      </span>
    </td>
    <td class="Table__Cell py-2 text-gray-400">
      <span>
        <aw-icon
          icon="info"
          class="w-5 h-5 current-fill cursor-pointer"
        />
        <document-element-file-metadata-tippy
          :antivirus-state="antivirusState"
          :element="element"
        />
      </span>
    </td>
    <td class="Table__Cell py-2 pl-2 pr-2.5 w-16">
      <document-element-file-download-link
        v-if="data.can_download && isDownloadable"
        :gallery="false"
        :is-pill="true" 
        :file="element.file"
      />
      <span
        v-else
        class="block w-16"
      />
    </td>
    <td
      class="py-2 px-3 pl-0 text-right whitespace-nowrap FileLastCell relative"
    >
      <span class="FileCircleWrapper">
        <span
          ref="DocumentCircle"
          class="DocumentCircle DocumentCircle--SM mr-2.5"
          :class="{
            'DocumentCircle--active': bcSelectedStore.selected.has(element.id)
          }"
          :data-item-index="element.root_rank"
          @click.prevent.stop="bcSelectedStore.select(
            element, 
            {
              isRecursive: true,
              isShift: $event.shiftKey,
              isDocument: true,
              itemIndex: element.root_rank,
            }
          )"
        ></span>
      </span>
      <span
        class="cursor-move text-gray-400 hover:text-gray-500"
        v-if="edit && parent.data.sort === 'rank' && !element.file?.deleted"
        :ref="dragHandle"
      >
        <aw-icon icon="dots" class="w-4 h-4 -mx-1 "></aw-icon>
        <aw-icon icon="dots" class="w-4 h-4 -mx-1 "></aw-icon>
      </span>
      <span v-else-if="edit" class="w-4.5 inline-block"></span>
    </td>
  </aw-drag>
</template>

<script>
import { FileApi } from '../../test-api/index.ts'
import FileState from '../../mixins/FileState'
import FileElement from '../../mixins/FileElement'
import ImagePreview from '../../mixins/ImagePreview'
import Selected from '../../mixins/Selected'
import debounce from 'lodash/debounce'

const fileApi = new FileApi({}, '/api/v2')

export default {
  mixins: [
    FileState,
    FileElement,
    ImagePreview,
    Selected,
  ],
  emits: ['on-open-image', 'on-preview-image-id'],
  props: {
    index: Number,
    currentImage: Object,
    eyeSelected: Boolean
  },
  methods: {
    openImage() {
      this.$emit('on-open-image')
    },
    setPreviewImageId(id) {
      this.$emit('on-preview-image-id', id)
    },
  },
  computed: {
    tagModel: {
      get() {
        return this.element?.file?.tags || []
      },
      async set(val) {
        if (!this.element.file || !this.edit) {
          return
        }

        this.element.file.tags = val

        if (this.debounceUpdate === null) {
          this.debounceUpdate = debounce((tags) => {
            fileApi
              .fileUpdate(this.element.file.id, { tags })
              .then((/*response*/) => {
                //this.element.file = response.data
              })
          })
        }

        this.debounceUpdate(val)
      }
    },
    tableTagsShow: {
      get() {
        return this.showTableTags
      },
      set(val) {
        clearTimeout(this.delayTimeout)
        this.delayTimeout = setTimeout(
          () => {
            this.showTableTags = val
          },
          val === false ? 100 : 300
        )
      }
    },
    userPictogramTippy() {
      let content = ''
      if (this.element.file.uploaded) {
        content =
          this.formatDate(this.element.file.uploaded, 'd. m. Y H:i') + '<br />'
      }

      if (this.element.file.uploaded_by__user) {
        content = content + this.element.file.uploaded_by__user.label
      }

      return {
        content,
        allowHTML: true,
        touch: true
      }
    }
  },
  mounted() {
    this.assignItemToSelectBtn('DocumentCircle', this.element)
  },
  watch: {
    'element.file.previews': {
      async handler(val) {
        if (val && val['500x500']) {
          await this.doLoadImagePreview('500x500')
        }
      },
      immediate: true,
    }
  }
}
</script>

<style lang="scss">
.HasTableTags .FileIcon {
  max-width: 50px;
}

.Table__Cell-Extension {
  max-width: 50px;
}
</style>
